import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toastService as toast } from '../services/toastService';
import { getApiErrorStatus } from '../utils/apiError';
import { Message, ChatRole, QuickReply, OnboardingStepId, OnboardingData } from '../types';
import { Logger } from '../utils/logger';
import {
  getOnboardingSteps,
  getNextStep,
  generateSummary,
  OnboardingTranslations
} from '../config/onboardingFlow';
import { useAuthContext } from '../contexts/AuthContext';
import { brandConfigService } from '../services/brandConfigService';

// Type pour les traductions complètes du contexte
interface FullTranslations {
  onboarding: OnboardingTranslations['onboarding'] & {
    generating?: string;
    generationSuccess?: string;
    generationError?: string;
    urlAnalyzing?: string;
    urlErrorContinue?: string;
    urlInvalid?: string;
    describeGoal?: string;
  };
}

// V4-FRONT-ANY-USAGE batch 6 2026-05-29 — shape (snake_case) renvoyé par
// POST /positioning/extract-from-url. Tous les champs sont optionnels :
// le backend soft-fail (422 url_scraping_failed) renvoie `extracted: undefined`.
interface UrlExtractionResult {
  extracted?: {
    company_name?: string;
    sector?: string;
    value_proposition?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface UsePositioningOnboardingProps {
  t: FullTranslations;
  language: string;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  extractFromUrl: (url: string, spaceId?: string) => Promise<UrlExtractionResult>;
  enrichPositioning: (data: OnboardingData, language: string) => Promise<{ positioning: string; cached?: boolean }>;
  updatePositioning: (content: string, source: string, spaceId?: string) => Promise<void>;
  onUpdatePrompt: (prompt: string) => void;
  setEditedPrompt: (prompt: string) => void;
  setIsTemplate: (value: boolean) => void;
}

interface UsePositioningOnboardingReturn {
  onboardingActive: boolean;
  setOnboardingActive: (active: boolean) => void;
  currentStep: OnboardingStepId;
  onboardingData: OnboardingData;
  waitingForUrlInput: boolean;
  waitingForTextInput: boolean;
  currentInputPlaceholder: string;
  isExtracting: boolean;
  onboardingSteps: ReturnType<typeof getOnboardingSteps>;
  canGoBack: boolean;
  handleQuickReplySelect: (reply: QuickReply) => Promise<void>;
  handlePreviousStep: () => void;
  handleOnboardingInput: (input: string) => Promise<void>;
  forceStartOnboarding: () => void;
  initializeOnboarding: (isTemplate: boolean) => void;
}

/**
 * Hook pour gérer la logique d'onboarding du positionnement
 * Extrait de PositioningTab pour améliorer la maintenabilité
 */
export function usePositioningOnboarding({
  t,
  language,
  messages,
  setMessages,
  extractFromUrl,
  enrichPositioning,
  updatePositioning,
  onUpdatePrompt,
  setEditedPrompt,
  setIsTemplate,
}: UsePositioningOnboardingProps): UsePositioningOnboardingReturn {

  // UX fix 20-05 — Récupère le space courant pour pouvoir déclencher en parallèle
  // l'extraction de la charte graphique (couleurs/fonts/images) quand l'utilisateur
  // partage une URL pendant l'onboarding. Sans ça, l'utilisateur partage mintera.co
  // pour le positionnement mais doit re-coller l'URL dans l'onglet "Charte graphique".
  const { currentSpace } = useAuthContext();

  // Générer les étapes d'onboarding avec les traductions (mémoïsé)
  const onboardingSteps = useMemo(
    () => getOnboardingSteps({ onboarding: t.onboarding } as OnboardingTranslations),
    [t]
  );
  
  // State d'onboarding
  const [onboardingActive, setOnboardingActive] = useState(true);
  const [currentStep, setCurrentStep] = useState<OnboardingStepId>('welcome');
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({});
  const [waitingForUrlInput, setWaitingForUrlInput] = useState(false);
  const [waitingForTextInput, setWaitingForTextInput] = useState(false);
  const [currentInputPlaceholder, setCurrentInputPlaceholder] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  // Pile d'historique pour le retour arrière : snapshot {stepId, data} pris AVANT chaque
  // transition. handlePreviousStep dépile pour restaurer l'étape précédente.
  const [stepHistory, setStepHistory] = useState<Array<{ stepId: OnboardingStepId; data: OnboardingData }>>([]);

  /**
   * Initialise l'onboarding basé sur si c'est un template ou non
   */
  const initializeOnboarding = useCallback((isTemplate: boolean) => {
    if (isTemplate) {
      const welcomeStep = onboardingSteps['welcome'];
      if (welcomeStep) {
        const welcomeMessage: Message = {
          id: 'onboarding-welcome-' + Date.now(),
          role: ChatRole.ASSISTANT,
          content: welcomeStep.message,
          quickReplies: welcomeStep.quickReplies,
          onboardingStep: 'welcome',
          timestamp: new Date().toISOString(),
        };
        setMessages([welcomeMessage]);
        setOnboardingActive(true);
        setCurrentStep('welcome');
        setStepHistory([]);
        Logger.log('[Positioning] Onboarding started with welcome message');
      }
    } else {
      setOnboardingActive(false);
      Logger.log('[Positioning] Existing positioning, onboarding disabled');
    }
  }, [onboardingSteps, setMessages]);

  /**
   * Force le démarrage de l'onboarding
   */
  const forceStartOnboarding = useCallback(() => {
    const welcomeStep = onboardingSteps['welcome'];
    if (welcomeStep) {
      const welcomeMessage: Message = {
        id: 'onboarding-force-' + Date.now(),
        role: ChatRole.ASSISTANT,
        content: welcomeStep.message,
        quickReplies: welcomeStep.quickReplies,
        onboardingStep: 'welcome',
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
      setOnboardingActive(true);
      setCurrentStep('welcome');
      setOnboardingData({});
      setStepHistory([]);
      Logger.log('[Positioning] Onboarding forced by user');
    }
  }, [onboardingSteps, setMessages]);

  /**
   * Avance vers une nouvelle étape d'onboarding
   */
  const advanceToStep = useCallback(async (stepId: OnboardingStepId, data: OnboardingData) => {
    setCurrentStep(stepId);
    const nextStep = onboardingSteps[stepId];
    
    if (!nextStep) {
      setOnboardingActive(false);
      return;
    }

    // Construire le message, remplacer {summary} si nécessaire
    let messageContent = nextStep.message;
    if (stepId === 'summary') {
      messageContent = messageContent.replace('{summary}', generateSummary(data, { onboarding: t.onboarding } as OnboardingTranslations));
    }

    // Ajouter le message de l'agent avec délai pour l'effet "typing"
    setTimeout(() => {
      const agentMessage: Message = {
        id: `onboarding-${stepId}-${Date.now()}`,
        role: ChatRole.ASSISTANT,
        content: messageContent,
        quickReplies: nextStep.quickReplies,
        onboardingStep: stepId,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, agentMessage]);

      // Si l'étape attend un input texte
      if (nextStep.inputType && !nextStep.quickReplies) {
        setWaitingForTextInput(true);
        setCurrentInputPlaceholder(nextStep.inputPlaceholder || '');
      }
    }, 500);

    // Si c'est l'étape finale
    if (stepId === 'complete') {
      setOnboardingActive(false);
    }
  }, [onboardingSteps, t, setMessages]);

  /**
   * Retour arrière dans le wizard : dépile le dernier snapshot, restaure l'étape et les
   * données collectées à ce moment, et rembobine le chat jusqu'au message de l'agent de
   * cette étape (les réponses postérieures sont retirées). No-op si aucun historique.
   */
  const handlePreviousStep = useCallback(() => {
    if (stepHistory.length === 0) return;
    const { stepId, data } = stepHistory[stepHistory.length - 1];
    setStepHistory(prev => prev.slice(0, -1));
    setCurrentStep(stepId);
    setOnboardingData(data);

    // Tronquer les messages jusqu'au message de l'agent de cette étape (inclus).
    setMessages(msgs => {
      let idx = -1;
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].onboardingStep === stepId) { idx = i; break; }
      }
      return idx >= 0 ? msgs.slice(0, idx + 1) : msgs;
    });

    // Réactiver le bon mode d'input pour l'étape restaurée.
    const step = onboardingSteps[stepId];
    if (step?.inputType && !step.quickReplies) {
      setWaitingForTextInput(true);
      setWaitingForUrlInput(stepId === 'company_url');
      setCurrentInputPlaceholder(step.inputPlaceholder || '');
    } else {
      setWaitingForTextInput(false);
      setWaitingForUrlInput(false);
      setCurrentInputPlaceholder('');
    }
  }, [stepHistory, onboardingSteps, setMessages]);

  /**
   * Génère le positionnement final à partir des données collectées via LLM
   */
  const handleGeneratePositioning = useCallback(async (data: OnboardingData) => {
    try {
      // Message de génération en cours
      const generatingMessage: Message = {
        id: 'generating-' + Date.now(),
        role: ChatRole.ASSISTANT,
        content: t.onboarding?.generating || "✨ Je génère votre positionnement personnalisé...",
        isTyping: true,
      };
      setMessages(prev => [...prev, generatingMessage]);

      // Appeler le LLM pour générer un positionnement enrichi
      const result = await enrichPositioning(data, language);
      const positioning = result.positioning;
      
      if (result.cached) {
        Logger.log('[Positioning] Used cached result');
      }
      
      // Sauvegarder le positionnement (V4-AGENCY-1 : on cible le space courant
      // pour éviter d'écrire sur un autre space si le user en a plusieurs).
      await updatePositioning(positioning, 'onboarding', currentSpace?.id);
      onUpdatePrompt(positioning);
      setEditedPrompt(positioning);
      setIsTemplate(false);

      // Remplacer le message de génération par le message de succès
      const successStep = onboardingSteps['complete'];
      const completeMessage: Message = {
        id: 'complete-' + Date.now(),
        role: ChatRole.ASSISTANT,
        content: successStep.message,
        onboardingStep: 'complete',
      };
      setMessages(prev => prev.filter(m => m.id !== generatingMessage.id).concat(completeMessage));
      
      setOnboardingActive(false);
      toast.success(t.onboarding?.generationSuccess || 'Positionnement généré avec succès !');
    } catch (error) {
      Logger.error('Error generating positioning:', error);
      setMessages(prev => prev.filter(m => !m.id.startsWith('generating-')));
      toast.error(t.onboarding?.generationError || 'Erreur lors de la génération du positionnement');
    }
  }, [enrichPositioning, language, updatePositioning, onUpdatePrompt, setEditedPrompt, setIsTemplate, t, onboardingSteps, setMessages, currentSpace?.id]);

  /**
   * Gère la sélection d'une réponse rapide (QuickReply)
   */
  const handleQuickReplySelect = useCallback(async (reply: QuickReply) => {
    if (!onboardingActive) return;

    // Ajouter le message utilisateur
    const userMessage: Message = {
      id: Date.now().toString(),
      role: ChatRole.USER,
      content: reply.text,
    };
    setMessages(prev => [...prev, userMessage]);

    // Mettre à jour les données d'onboarding
    const step = onboardingSteps[currentStep];
    let updatedData = { ...onboardingData };
    
    if (step.dataField) {
      // @ts-ignore - Typage dynamique
      updatedData[step.dataField] = reply.value;
      setOnboardingData(updatedData);
    }

    // Si c'est une action spéciale
    if (reply.action === 'url_input') {
      setWaitingForUrlInput(true);
      setCurrentInputPlaceholder(onboardingSteps['company_url'].inputPlaceholder || 'https://...');
      return;
    }

    if (reply.action === 'text_input') {
      setWaitingForTextInput(true);
      const stepPlaceholder = onboardingSteps[currentStep]?.inputPlaceholder;
      setCurrentInputPlaceholder(stepPlaceholder || t.onboarding?.describeGoal || 'Describe your goal...');
      return;
    }

    if (reply.action === 'confirm') {
      await handleGeneratePositioning(updatedData);
      return;
    }

    if (reply.value === 'restart') {
      setOnboardingData({});
      setCurrentStep('welcome');
      setStepHistory([]);
      const welcomeStep = onboardingSteps['welcome'];
      const welcomeMessage: Message = {
        id: 'onboarding-restart-' + Date.now(),
        role: ChatRole.ASSISTANT,
        content: welcomeStep.message,
        quickReplies: welcomeStep.quickReplies,
        onboardingStep: 'welcome',
      };
      setMessages([welcomeMessage]);
      return;
    }

    // Historiser l'état AVANT la transition pour permettre le retour arrière.
    setStepHistory(prev => [...prev, { stepId: currentStep, data: onboardingData }]);
    // Passer à l'étape suivante
    const nextStepId = getNextStep(onboardingSteps, currentStep, reply.value);
    await advanceToStep(nextStepId, updatedData);
  }, [onboardingActive, currentStep, onboardingData, onboardingSteps, t, setMessages, handleGeneratePositioning, advanceToStep]);

  /**
   * Gère la soumission d'un input texte/URL pendant l'onboarding
   */
  const handleOnboardingInput = useCallback(async (inputValue: string) => {
    if (!inputValue.trim()) return;

    const userInput = inputValue.trim();
    setWaitingForTextInput(false);
    setWaitingForUrlInput(false);

    // Ajouter le message utilisateur
    const userMessage: Message = {
      id: Date.now().toString(),
      role: ChatRole.USER,
      content: userInput,
    };
    setMessages(prev => [...prev, userMessage]);

    // Historiser l'état AVANT la saisie pour permettre le retour arrière.
    setStepHistory(prev => [...prev, { stepId: currentStep, data: onboardingData }]);

    // Mettre à jour les données selon l'étape
    const step = onboardingSteps[currentStep];
    let updatedData = { ...onboardingData };

    if (step.dataField) {
      if (step.dataField === 'values') {
        updatedData.values = userInput.split(',').map(v => v.trim()).filter(v => v);
      } else {
        // @ts-ignore
        updatedData[step.dataField] = userInput;
      }
      setOnboardingData(updatedData);
    }

    // Si c'était une URL, essayer de l'analyser
    if (waitingForUrlInput && currentStep === 'company_url') {
      // Normaliser l'URL : ajouter https:// si aucun protocole fourni
      let urlToExtract = userInput.trim();
      if (urlToExtract && !urlToExtract.startsWith('http://') && !urlToExtract.startsWith('https://')) {
        urlToExtract = 'https://' + urlToExtract;
      }

      // Valider le format URL avant l'appel API
      const urlPattern = /^https?:\/\/.+\..+/;
      if (!urlToExtract.match(urlPattern)) {
        const invalidUrlMessage: Message = {
          id: 'invalid-url-' + Date.now(),
          role: ChatRole.ASSISTANT,
          content: t.onboarding?.urlInvalid || "⚠️ Format d'URL invalide. Entrez le nom et l'activité de votre entreprise manuellement.",
        };
        setMessages(prev => [...prev, invalidUrlMessage]);
        setTimeout(() => advanceToStep('company_name', updatedData), 1000);
        return;
      }

      let extractionSucceeded = false;
      try {
        setIsExtracting(true);
        const analyzingMessage: Message = {
          id: 'analyzing-' + Date.now(),
          role: ChatRole.ASSISTANT,
          content: t.onboarding?.urlAnalyzing || "🔍 J'analyse votre site web...",
          isTyping: true,
        };
        setMessages(prev => [...prev, analyzingMessage]);

        // UX fix 20-05 — Déclenche en parallèle l'extraction de la charte graphique
        // (couleurs/fonts/images) pour la même URL. Fire-and-forget : on n'attend
        // PAS la fin (le job arq tourne en background côté backend), on log
        // simplement le déclenchement. Le composant BrandConfigContent verra la
        // charte apparaître via son propre polling quand l'utilisateur ouvre
        // l'onglet "Charte graphique".
        if (currentSpace?.id) {
          brandConfigService
            .extract(currentSpace.id, urlToExtract)
            .then(() => {
              Logger.info('[onboarding] Brand config extraction triggered', { url: urlToExtract });
            })
            .catch((err) => {
              // Non-bloquant : on ne casse pas l'onboarding si l'extract de charte échoue.
              Logger.warn('[onboarding] Brand config extraction failed to trigger', err);
            });
        }

        // V4-AGENCY-3-OTHER (2026-05-21) : on cible explicitement le space
        // courant pour la persistance d'URL côté backend (sinon écrasement
        // possible sur le mauvais space en multi-spaces).
        const result = await extractFromUrl(urlToExtract, currentSpace?.id);

        if (result?.extracted) {
          extractionSucceeded = true;
          updatedData.extractedData = result.extracted;
          updatedData.companyName = result.extracted.company_name;
          updatedData.sector = result.extracted.sector;
          setOnboardingData(updatedData);

          const extractedMessage: Message = {
            id: 'extracted-' + Date.now(),
            role: ChatRole.ASSISTANT,
            content: `✅ J'ai trouvé ces informations sur votre site :\n\n• **Entreprise** : ${result.extracted.company_name || 'Non détecté'}\n• **Secteur** : ${result.extracted.sector || 'Non détecté'}\n• **Proposition de valeur** : ${result.extracted.value_proposition || 'Non détectée'}\n\nJe vais utiliser ces informations pour personnaliser votre positionnement !`,
          };
          setMessages(prev => prev.filter(m => m.id !== analyzingMessage.id).concat(extractedMessage));
        } else {
          // Extraction sans données utiles — fallback vers saisie manuelle
          const noDataMessage: Message = {
            id: 'no-data-' + Date.now(),
            role: ChatRole.ASSISTANT,
            content: t.onboarding?.urlErrorContinue || "⚠️ Je n'ai pas pu analyser ce site. Entrez le nom de votre entreprise manuellement.",
          };
          setMessages(prev => prev.filter(m => !m.isTyping).concat(noDataMessage));
        }
      } catch (error: unknown) {
        // V4-POS-06 2026-05-29 — distinguer le 422 `url_scraping_failed` (backend
        // a soft-failé l'extraction, on continue gracieusement avec l'IA) vs un
        // vrai 5xx (le toast d'erreur global de apiClient se déclenchera côté
        // utilisateur — pas idéal mais hors scope de ce hook). Dans les 2 cas
        // on affiche le même message d'invite manuelle dans le chat — l'AI
        // gère le fallback narratif.
        // V4-FRONT-ANY-USAGE batch 6 2026-05-29
        const status = getApiErrorStatus(error);
        const data = axios.isAxiosError(error)
          ? (error.response?.data as { detail?: { error?: string }; error?: string } | undefined)
          : undefined;
        const errorCode = data?.detail?.error || data?.error;
        const isSoftScrapingFail = status === 422 && errorCode === 'url_scraping_failed';
        if (isSoftScrapingFail) {
          Logger.info('[Positioning] URL scraping soft-failed (422) — graceful fallback');
        } else {
          Logger.error('Error extracting URL:', error);
        }
        const errorMessage: Message = {
          id: 'error-' + Date.now(),
          role: ChatRole.ASSISTANT,
          content: t.onboarding?.urlErrorContinue || "⚠️ Je n'ai pas pu analyser ce site. Pas de souci, entrez le nom de votre entreprise manuellement !",
        };
        setMessages(prev => prev.filter(m => !m.isTyping).concat(errorMessage));
      } finally {
        setIsExtracting(false);
      }

      // Navigation conditionnelle : succès → purpose, échec → company_name
      setTimeout(() => {
        advanceToStep(extractionSucceeded ? 'purpose' : 'company_name', updatedData);
      }, 1000);
      return;
    }

    // Passer à l'étape suivante
    const nextStepId = getNextStep(onboardingSteps, currentStep, 'default');
    await advanceToStep(nextStepId, updatedData);
  }, [currentStep, onboardingData, waitingForUrlInput, extractFromUrl, advanceToStep, onboardingSteps, t, setMessages, currentSpace?.id]);

  return {
    onboardingActive,
    setOnboardingActive,
    currentStep,
    onboardingData,
    waitingForUrlInput,
    waitingForTextInput,
    currentInputPlaceholder,
    isExtracting,
    onboardingSteps,
    canGoBack: stepHistory.length > 0,
    handleQuickReplySelect,
    handlePreviousStep,
    handleOnboardingInput,
    forceStartOnboarding,
    initializeOnboarding,
  };
}
